import { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import '../../styles/page/ExpertCollabView.scss';
import { gql, useMutation, ApolloError } from '@apollo/client';
import {
  ExpertInvitePotentialExpertMutation,
  ExpertInvitePotentialExpertMutationVariables,
  ExpertDetailsQuery,
} from '../../gql/graphql';
import { GlobalNotificationContext } from '../context/GlobalNotification';
import { ExpertDetail } from '../../utils/gql';
import { FOCUS_OPTIONS } from '../../utils/constants';
import { tagPersonFocus } from '../../utils/format';
import logError from '../../utils/airbrake';
import errorUtils from '../../utils/error';

const expertInvitePotentialExpertMutation = gql`
  mutation ExpertInvitePotentialExpert(
    $email: String!
    $expertise: String!
    $name: String!
  ) {
    expertInvitePotentialExpert(
      email: $email
      expertise: $expertise
      name: $name
    ) {
      ...ExpertDetail
    }
  }
  ${ExpertDetail}
`;

interface ExpertCollabViewProps {
  expertDetails?: ExpertDetailsQuery['expertDetails'];
}

const ExpertCollabView = ({ expertDetails }: ExpertCollabViewProps) => {
  const { addNotification } = useContext(GlobalNotificationContext);
  const [actionLoading, setActionLoading] = useState(false);
  const [newExpertEmail, setNewExpertEmail] = useState('');
  const [newExpertExpertise, setNewExpertExpertise] = useState(
    'FOCUS_WEB_DEVELOPMENT',
  );
  const [newExpertName, setNewExpertName] = useState('');
  const [tryReferPotential] = useMutation<
    ExpertInvitePotentialExpertMutation,
    ExpertInvitePotentialExpertMutationVariables
  >(expertInvitePotentialExpertMutation);
  function doReferPotential() {
    if (actionLoading) return;
    if (!newExpertName || !newExpertExpertise || !newExpertEmail) {
      addNotification('Please submit all the fields', undefined, 5000);
      return;
    }
    setActionLoading(true);
    tryReferPotential({
      variables: {
        email: newExpertEmail,
        expertise: newExpertExpertise,
        name: newExpertName,
      },
    })
      .then(() => {
        setActionLoading(false);
        setNewExpertEmail('');
        setNewExpertExpertise('FOCUS_WEB_DEVELOPMENT');
        setNewExpertName('');
      })
      .catch((err: ApolloError) => {
        setActionLoading(false);
        addNotification(
          errorUtils.getErrorMessage(err) || 'Refer Expert Error',
        );
        logError(err, {
          component: 'ExpertCollabView',
          func: 'tryReferPotential',
        });
      });
  }
  return (
    <div className="DashboardScreen CollabScreen">
      <div className="DashboardScreenContent CollabScreenContent">
        <div className="DashboardSection">
          <div className="DashboardSectionHeader">
            Invite past collaborators
          </div>
          <div className="DashboardSectionAligned">
            <div className="CollabScreenSplit CollabScreenSplitLeft">
              <div className="AdminDashboardForm">
                <a
                  className="AdminDashboardFormActionLink"
                  href="https://stmembers.kb.help/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
                <div>
                  <div className="AdminDashboardFormTitle">Invites</div>
                  <div className="AdminDashboardFormSubtitle">
                    Do you know someone who should join Storetasker? We pay up
                    to $2,000 for each new member you refer.
                  </div>
                </div>
                <div className="AdminDashboardFormField">
                  <div className="AdminDashboardFormFieldTitle">Name</div>
                  <input
                    className="AdminDashboardFormFieldInput"
                    type="text"
                    autoComplete="new-off"
                    spellCheck="false"
                    placeholder="Name"
                    value={newExpertName}
                    onChange={(e) => setNewExpertName(e.currentTarget.value)}
                  />
                </div>
                <div className="AdminDashboardFormField">
                  <div className="AdminDashboardFormFieldTitle">
                    Email Address
                  </div>
                  <input
                    className="AdminDashboardFormFieldInput"
                    type="email"
                    autoComplete="new-off"
                    spellCheck="false"
                    placeholder="Email Address"
                    value={newExpertEmail}
                    onChange={(e) => setNewExpertEmail(e.currentTarget.value)}
                  />
                </div>
                <div className="AdminDashboardFormField">
                  <div className="AdminDashboardFormFieldTitle">Expertise</div>
                  <select
                    className="AdminDashboardFormFieldInput"
                    required
                    value={newExpertExpertise}
                    onChange={(e) =>
                      setNewExpertExpertise(e.currentTarget.value)
                    }
                  >
                    {FOCUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.person}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  className={
                    'AdminDashboardFormAction ' +
                    (actionLoading ? ' AdminDashboardFormActionLoading ' : '')
                  }
                  onClick={doReferPotential}
                >
                  Invite
                </div>
              </div>
            </div>
            <div className="CollabScreenSplit CollabScreenSplitRight">
              {!!expertDetails &&
                !!expertDetails.potentialExpertReferrals &&
                !!expertDetails.potentialExpertReferrals.length && (
                  <div className="CollabInvites">
                    <div className="CollabInvitesHeader">Your invitations</div>
                    <div className="CollabInvitesList">
                      {expertDetails.potentialExpertReferrals.map((r) => (
                        <div key={r.email} className="CollabInvitesListItem">
                          <div className="CollabInvitesListName">{r.name}</div>
                          <div className="CollabInvitesListTitle">
                            {tagPersonFocus(r.expertise)}
                          </div>
                          <div className="CollabInvitesListEmail">
                            {r.email}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ExpertCollabView.propTypes = {
  expertDetails: PropTypes.object,
};

ExpertCollabView.defaultProps = {
  expertDetails: null,
};

export default ExpertCollabView;
